<template>
  <BaseModal
    :title="tab === 'info' ? $t('Company information') : $t('Edit company information')"
    :subtitle="team ? team.name : ''"
    need-close-emit
    @close="closeModalHandler"
  >
    <template #header-action>
      <Button
        v-if="showEditSettingsButton"
        class="spacex-4 modal__header-btn"
        color="neutral"
        type="ghost"
        size="small"
        @click="goToEditSettings"
      >
        <div class="d-contents spacex-4">
          <EditIcon class="icon-gray" />
          <span>{{ $t('Edit settings') }}</span>
        </div>
      </Button>
    </template>
    <TeamSettingsMembers
      v-if="tab === 'info'"
      :team="team"
      :data-loading="dataLoading"
      @refetch="refetch"
    />
    <Form v-else-if="tab === 'settings' && form" class="form-window" @submit="submit">
      <FormInput
        id="company-name"
        v-model="form.name"
        :placeholder="$t('Enter your company name')"
        :label-text="$t('Company name')"
        required
        @input="markFormAsChanged"
      />
      <FormInput
        id="reg-num"
        v-model="form.registration_number"
        :placeholder="$t('Enter reg.number')"
        :label-text="$t('Registration number')"
        required
        @input="markFormAsChanged"
      />
      <FormInput
        id="address"
        v-model="form.address"
        :placeholder="$t('Enter address')"
        :label-text="$t('Company address')"
        required
        @input="markFormAsChanged"
      />
      <div class="two-form-items">
        <Autocomplete
          v-model="form.currency"
          :label-text="$t('Default currency')"
          :items="currencyItems"
        />
        <FormInput
          id="domain"
          v-model="form.company_domain"
          :label-text="$t('Company domain')"
          disabled
          :input-info="$t('Contact support if you need to change domain')"
          @input="markFormAsChanged"
        />
      </div>
    </Form>
    <template #footer>
      <div v-if="tab === 'settings'" class="d-contents flex-between flex-row-reverse width-100">
        <div class="spacex-8 flex-centering">
          <Button class="spacex-4" type="filled" color="neutral" @click="goBackToCompanyInfo">
            <div class="d-contents spacex-4">
              <span>{{ $t('Cancel') }}</span>
            </div>
          </Button>
          <Button class="spacex-4" @click="submit">
            <Spinner v-if="submitLoading" :size="20" />
            <span v-else>{{ $t('Save changes') }}</span>
          </Button>
        </div>
        <SuccessAlert v-if="success" class="mx-4" />
        <ErrorAlert v-else-if="error" :error="error" class="mx-4" />
      </div>
    </template>
  </BaseModal>
</template>

<script>
  import { mapActions, mapState } from 'vuex';
  import TeamSettingsMembers from '@/containers/top-menu/header/profile-settings/team/Members';
  import teamQuery from '@/gql/projects/team.graphql';
  import BaseModal from '@/components/ui/BaseModal';
  import Button from '@/components/ui/Button';
  import EditIcon from '@/assets/icons/pencil.svg';
  import Form from '@/components/common/Form';
  import FormInput from '@/components/ui/FormInput';
  import factory from '@/utils/api/factory';
  import parseError from '@/utils/formatting/parse-error';
  import get from 'lodash.get';
  import Autocomplete from '@/components/ui/Autocomplete';
  import SuccessAlert from '@/components/common/SuccessAlert';
  import ErrorAlert from '@/components/common/ErrorAlert';
  import Spinner from 'vue-simple-spinner';

  export default {
    name: 'TeamSettingsModal',
    components: {
      Spinner,
      Button,
      BaseModal,
      TeamSettingsMembers,
      EditIcon,
      Form,
      FormInput,
      Autocomplete,
      SuccessAlert,
      ErrorAlert,
    },
    apollo: {
      team() {
        this.dataLoading = true;
        return {
          query: teamQuery,
          fetchPolicy: 'no-cache',
          variables() {
            return {
              id: this.$store.state.auth?.user?.membership?.id,
            };
          },
          update(data) {
            this.dataLoading = false;
            if (data?.team?.length) {
              this.form = data.team[0];
              return data.team[0];
            }
            return { memberships: [] };
          },
        };
      },
    },
    data() {
      return {
        submitLoading: false,
        dataLoading: true,
        tab: get(this.$route, 'query.tab', 'info'),
        form: null,
        error: null,
        success: null,
        isFormChanged: false,
        CLOSE_FORM_CONFIRM_CONTEXT: {
          title: this.$t('Cancel changes to the company settings?'),
          actionText: this.$t('Cancel changes'),
          cancelText: this.$t('Return'),
          description: this.$t('All the changes you made in this window will be deleted'),
          style: 'danger',
        },
        items: ['foo', 'bar', 'fizz', 'buzz'],
        value: 'foo',
      };
    },
    computed: {
      ...mapState({
        activeCompany: (state) => state.auth.user?.membership?.id,
        user: (state) => state.auth.user,
        showEditSettingsButton() {
          return (
            this.tab === 'info' &&
            (this.user.membership.role === 'admin' || this.user.membership.role === 'owner')
          );
        },
        currencies: (state) => state.global.currencies,
      }),
      currencyItems() {
        return Object.entries(this.currencies).map(([value, text]) => {
          return {
            value,
            text,
          };
        });
      },
    },
    methods: {
      ...mapActions({
        reload: 'auth/load',
        closeModal: 'modal/closeModal',
        confirm: 'confirmModal/openModal',
      }),
      refetch() {
        this.$apollo.queries.team.refetch();
      },
      goToEditSettings() {
        this.tab = 'settings';
      },
      goBackToCompanyInfo() {
        if (this.isFormChanged) {
          this.confirm({
            ...this.CLOSE_FORM_CONFIRM_CONTEXT,
            action: () => {
              this.isFormChanged = false;
              this.tab = 'info';
              this.refetch();
            },
          });
        } else {
          this.tab = 'info';
        }
      },
      async closeModalHandler() {
        if (this.tab === 'settings' && this.isFormChanged) {
          this.confirm({
            ...this.CLOSE_FORM_CONFIRM_CONTEXT,
            action: () => {
              this.isFormChanged = false;
              this.closeModal();
            },
          });
        } else {
          this.closeModal();
        }
        let query = Object.assign({}, this.$route.query);
        delete query.modal;
        delete query.tab;
        await this.$router.replace({ query });
      },
      markFormAsChanged() {
        this.isFormChanged = true;
      },
      async submit() {
        this.submitLoading = true;
        this.error = null;
        this.success = null;

        try {
          await factory('teams').patchInPath(this.activeCompany, this.form);
          this.success = this.$t('Changes have been successfully saved');
          this.submitLoading = false;
          this.isFormChanged = false;
          await this.reload({ silent: true });
        } catch (e) {
          console.error(e); // eslint-disable-line no-console
          this.error = parseError(e) || 'Something went wrong.';
          this.submitLoading = false;
        }
      },
    },
  };
</script>
